<div class="max-w-4xl mx-auto px-4 py-12">
  <div class="prose max-w-none">
    <h1 class="text-4xl font-bold mb-6">Lei Geral de Proteção de Dados Pessoais (LGPD)</h1>

    <p class="text-gray-600 mb-8">
      <strong>Data de Atualização:</strong> {{ currentYear }}<br>
      <strong>Última Revisão:</strong> {{ currentYear }}
    </p>

    <div class="border border-gray-200 rounded-xl p-6 mb-10 bg-white shadow-sm">
      <h2 class="text-xl font-semibold mb-4">Sumário</h2>
      <div class="toc-grid grid gap-2 sm:grid-cols-2">
        <a class="text-blue-700 hover:text-blue-900 transition" href="#informacoes">1. Informações sobre a LGPD</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#conceitos">2. Conceitos Fundamentais</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#bases">3. Bases Legais</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#direitos">4. Direitos dos Titulares</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#obrigacoes">5. Obrigações da Plataforma</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#dpo">6. Encarregado de Proteção de Dados</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#procedimento">7. Procedimento para Exercer Direitos</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#transferencia">8. Transferência Internacional</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#sensiveis">9. Proteção de Dados Sensíveis</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#menores">10. Proteção de Dados de Menores</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#anpd">11. ANPD</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#sancoes">12. Sanções</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#faq">13. Perguntas Frequentes</a>
        <a class="text-blue-700 hover:text-blue-900 transition" href="#conclusao">14. Conclusão</a>
      </div>
    </div>

    <section id="informacoes" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">1. Informações sobre a LGPD</h2>
      <p class="mb-4">
        A Lei Geral de Proteção de Dados Pessoais (LGPD) é a Lei nº 13.709, sancionada em 14 de agosto de 2018 e
        em vigor desde 18 de setembro de 2020, que estabelece regras sobre coleta, processamento, armazenamento,
        transferência, utilização e exclusão de dados pessoais de pessoas naturais, com o objetivo de proteger
        os direitos fundamentais de liberdade e privacidade.
      </p>
      <p class="mb-4">
        Esta página fornece informações sobre como nossa Plataforma se adequa aos requisitos da LGPD e orienta
        você sobre seus direitos como titular de dados pessoais.
      </p>
    </section>

    <section id="conceitos" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">2. Conceitos Fundamentais da LGPD</h2>
      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">2.1 Dados Pessoais</h3>
        <p class="mb-4">
          Conforme Art. 5º da LGPD, "dado pessoal" é qualquer informação relacionada a pessoa natural identificada
          ou identificável. Isso inclui informações como nome, CPF, e-mail, endereço, telefone, data de nascimento,
          entre outros.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">2.2 Dados Sensíveis</h3>
        <p class="mb-4">
          Conforme Art. 5º da LGPD, "dado pessoal sensível" é um tipo especial de dado pessoal que merece proteção
          reforçada, incluindo:
        </p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Dados referentes à origem racial ou étnica</li>
          <li>Convicção religiosa</li>
          <li>Opinião política</li>
          <li>Filiação a sindicato ou organização religiosa</li>
          <li>Dados genéticos</li>
          <li>Dados biométricos coletados com fim de identificação</li>
          <li>Dados relativos à saúde</li>
          <li>Dados relativos à vida sexual ou orientação sexual</li>
        </ul>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">2.3 Controlador e Operador</h3>
        <p class="mb-4">
          <strong>Controlador (Art. 5º):</strong> Pessoa natural ou jurídica, de direito público ou privado, que toma
          as decisões sobre o processamento de dados pessoais. Em nosso caso, a organizadora do concurso é geralmente
          o controlador dos dados de inscrição.
        </p>
        <p class="mb-4">
          <strong>Operador (Art. 5º):</strong> Pessoa natural ou jurídica, de direito público ou privado, que processa
          dados pessoais em nome do controlador. Nossa Plataforma pode atuar como operador de dados em nome das
          organizadoras de concursos.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">2.4 Consentimento</h3>
        <p class="mb-4">
          Conforme Art. 5º da LGPD, consentimento é a "manifestação livre, informada e inequívoca pela qual o titular
          concorda com o processamento de seus dados pessoais para uma finalidade determinada".
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">2.5 Encarregado de Proteção de Dados (DPO)</h3>
        <p class="mb-4">
          Conforme Art. 5º da LGPD, o encarregado é a "pessoa indicada pelo controlador e pelo operador para atuar
          como canal de comunicação entre o controlador, os operadores, os titulares dos dados e a Autoridade Nacional
          de Proteção de Dados (ANPD)".
        </p>
      </div>
    </section>

    <section id="bases" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">3. Bases Legais para Processamento de Dados</h2>
      <p class="mb-4">
        Conforme Art. 7º da LGPD, o processamento de dados pessoais é permitido apenas quando há uma base legal.
        Nossa Plataforma utiliza as seguintes bases legais:
      </p>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">3.1 Consentimento (Art. 7º, I)</h3>
        <p class="mb-4">
          Obtemos consentimento livre, informado e inequívoco para o processamento de dados pessoais. Você pode retirar
          seu consentimento a qualquer momento, sem qualquer consequência negativa além da impossibilidade de continuar
          usando serviços que dependem daquele processamento específico.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">3.2 Contrato (Art. 7º, V)</h3>
        <p class="mb-4">
          O processamento é necessário para a execução de contrato com você, como para processar sua inscrição em um concurso.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">3.3 Obrigação Legal (Art. 7º, II)</h3>
        <p class="mb-4">
          O processamento é necessário para cumprir obrigações legais impostas por lei, como a manutenção de registros
          para fins fiscais e trabalhistas.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">3.4 Interesses Legítimos (Art. 7º, IX)</h3>
        <p class="mb-4">
          O processamento é necessário para proteção de interesses legítimos do controlador ou de terceiros, como
          prevenção de fraudes e garantia da segurança da Plataforma.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">3.5 Exercício de Direitos (Art. 7º, VI)</h3>
        <p class="mb-4">
          O processamento é necessário para o exercício regular de direitos em contrato, processo judicial ou procedimento
          administrativo.
        </p>
      </div>
    </section>

    <section id="direitos" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">4. Direitos dos Titulares de Dados Pessoais</h2>
      <p class="mb-4">
        Conforme Art. 18 da LGPD, são direitos do titular de dados pessoais:
      </p>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">4.1 Direito de Confirmação e Acesso (Art. 18, I e II)</h3>
        <p class="mb-4">
          Você tem o direito de obter do controlador confirmação de que seus dados pessoais estão sendo processados
          e acesso a esses dados, com informações específicas sobre o processamento.
        </p>
        <p class="mb-4">
          <strong>Como exercer:</strong> Envie uma solicitação para nosso Encarregado de Proteção de Dados (DPO) indicando
          que deseja acessar seus dados pessoais. Responderemos em até 15 (quinze) dias (prorrogáveis por mais 15 dias,
          totalizando até 30 dias).
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">4.2 Direito de Retificação (Art. 18, III)</h3>
        <p class="mb-4">
          Você tem o direito de solicitar a correção de dados pessoais inexatos ou a complementação de dados incompletos.
        </p>
        <p class="mb-4">
          <strong>Como exercer:</strong> Entre em contato conosco para solicitar a correção de seus dados. Faremos a
          alteração e comunicaremos a todos os operadores que processam seus dados.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">4.3 Direito à Exclusão (Art. 17)</h3>
        <p class="mb-4">
          Você tem o direito de solicitar a exclusão de seus dados pessoais quando:
        </p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Os dados não forem mais necessários para a finalidade original</li>
          <li>Você retirar seu consentimento</li>
          <li>Os dados tiverem sido processados ilegalmente</li>
          <li>Exigido por obrigação legal</li>
        </ul>
        <p class="mb-4">
          <strong>Como exercer:</strong> Solicite ao DPO a exclusão de seus dados. Tomaremos as medidas necessárias para
          excluir seus dados, salvo quando houver obrigação legal de retenção.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">4.4 Direito à Limitação de Processamento (Art. 18, IV)</h3>
        <p class="mb-4">
          Você tem o direito de solicitar que o processamento de seus dados seja limitado quando:
        </p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>A precisão dos dados for questionada</li>
          <li>O processamento for realizado ilegalmente</li>
          <li>Os dados não forem mais necessários para a finalidade</li>
          <li>Você se opor ao processamento</li>
        </ul>
        <p class="mb-4">
          <strong>Como exercer:</strong> Notifique-nos sobre a limitação desejada e os motivos. Marcaremos os dados
          para limitação enquanto investigamos.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">4.5 Direito à Portabilidade (Art. 20)</h3>
        <p class="mb-4">
          Você tem o direito de receber seus dados pessoais em um formato estruturado, comumente utilizado e legível
          por máquina, e de transmiti-los para outro controlador.
        </p>
        <p class="mb-4">
          <strong>Como exercer:</strong> Solicite ao DPO seus dados em formato portável (geralmente CSV ou JSON).
          Forneceremos seus dados dentro de 15 dias (prorrogáveis para 30 dias).
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">4.6 Direito à Oposição (Art. 21)</h3>
        <p class="mb-4">
          Você tem o direito de se opor ao processamento de seus dados quando baseado em interesse legítimo ou para fins
          de marketing direto.
        </p>
        <p class="mb-4">
          <strong>Como exercer:</strong> Notifique-nos de sua oposição e os motivos. Avaliaremos sua objeção e tomaremos
          medidas apropriadas.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">4.7 Direito a Não Sofrer Decisões Automatizadas (Art. 20)</h3>
        <p class="mb-4">
          Você tem o direito de não ser submetido a decisões tomadas unicamente por meios automatizados que produzam
          efeitos legais ou que o afetem significativamente.
        </p>
        <p class="mb-4">
          <strong>Como exercer:</strong> Se você for afetado por uma decisão automatizada, entre em contato conosco
          para solicitar revisão humana da decisão.
        </p>
      </div>
    </section>

    <section id="obrigacoes" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">5. Obrigações da Plataforma conforme a LGPD</h2>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">5.1 Transparência e Informação (Art. 9º)</h3>
        <p class="mb-4">
          Somos obrigados a fornecer informações claras, precisas e facilmente acessíveis sobre o processamento de
          seus dados pessoais. Cumprimos isso através desta página, da Política de Privacidade e de avisos durante
          o processamento de dados.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">5.2 Segurança de Dados (Art. 32)</h3>
        <p class="mb-4">
          Somos obrigados a implementar medidas técnicas e administrativas apropriadas para proteger seus dados pessoais
          contra acesso não autorizado, alteração acidental ou não autorizada e destruição. Implementamos:
        </p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>Criptografia de dados em trânsito (SSL/TLS)</li>
          <li>Criptografia de dados em repouso</li>
          <li>Firewalls e sistemas de prevenção de intrusão</li>
          <li>Autenticação forte (senhas criptografadas, autenticação de dois fatores)</li>
          <li>Controle de acesso baseado em funções</li>
          <li>Monitoramento contínuo de segurança</li>
          <li>Testes de segurança regulares</li>
        </ul>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">5.3 Notificação de Incidentes (Art. 33 e 34)</h3>
        <p class="mb-4">
          Se ocorrer um incidente de segurança que comprometa seus dados pessoais, somos obrigados a notificar:
        </p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>A Autoridade Nacional de Proteção de Dados (ANPD) - em até 2 (dois) dias úteis</li>
          <li>Os titulares de dados afetados - sem atraso injustificado</li>
        </ul>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">5.4 Avaliação de Impacto (Art. 32, § 3º)</h3>
        <p class="mb-4">
          Para processamentos de alto risco, realizamos avaliações de impacto sobre a proteção de dados pessoais
          para identificar e mitigar riscos.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">5.5 Proteção de Dados desde o Design (Art. 10 e 32)</h3>
        <p class="mb-4">
          Implementamos proteção de dados desde o design de nossos sistemas e operações, considerando a privacidade
          como um requisito essencial.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">5.6 Registros de Processamento (Art. 5º, VIII)</h3>
        <p class="mb-4">
          Mantemos registros detalhados de todas as atividades de processamento de dados para fins de conformidade
          e auditoria.
        </p>
      </div>
    </section>

    <section id="dpo" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">6. Encarregado de Proteção de Dados (DPO)</h2>
      <p class="mb-4">
        Nossa Plataforma designou um Encarregado de Proteção de Dados (DPO - Data Protection Officer) para supervisionar
        a conformidade com a LGPD e servir como ponto de contato para todas as questões relacionadas à proteção de dados.
      </p>

      <div class="bg-gray-50 p-6 rounded-lg my-4">
        <p class="mb-2"><strong>Encarregado de Proteção de Dados (DPO)</strong></p>
        <p class="mb-2"><strong>E-mail:</strong> contato&#64;zoomeduca.com.br</p>
        <p class="mb-2"><strong>Telefone:</strong> +55 (86) 9 9981-2204</p>
        <!-- <p class="mb-2"><strong>Endereço:</strong> [Endereço completo da organização]</p>
        <p><strong>Horário de Atendimento:</strong> Segunda a sexta, 8h às 17h (horário de Brasília)</p> -->
      </div>

      <p class="mb-4">
        O DPO está disponível para:
      </p>
      <ul class="list-disc pl-6 mb-4 space-y-2">
        <li>Receber e processar solicitações de titulares sobre seus direitos LGPD</li>
        <li>Fornecer informações sobre processamento de dados</li>
        <li>Investigar reclamações sobre violações da LGPD</li>
        <li>Servir como canal de comunicação com a ANPD</li>
      </ul>
    </section>

    <section id="procedimento" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">7. Procedimento para Exercer Seus Direitos</h2>
      <ol class="list-decimal pl-6 mb-4 space-y-3">
        <li>
          <strong>Identifique seu direito:</strong> Determine qual direito LGPD você deseja exercer (acesso, retificação,
          exclusão, portabilidade, limitação, oposição).
        </li>
        <li>
          <strong>Prepare sua solicitação:</strong> Envie uma solicitação clara e específica para nosso DPO, incluindo:
          <ul class="list-disc pl-6 mt-2 space-y-1">
            <li>Seu nome completo e dados de identificação</li>
            <li>Descrição clara do direito que deseja exercer</li>
            <li>Razão ou justificativa (se aplicável)</li>
            <li>Data e assinatura ou confirmação da solicitação</li>
          </ul>
        </li>
        <li>
          <strong>Envie a solicitação:</strong> Dirija-se a nosso DPO pelos canais fornecidos na seção 6.
        </li>
        <li>
          <strong>Verificação de identidade:</strong> Podemos solicitar verificação adicional de identidade para
          proteger seus dados.
        </li>
        <li>
          <strong>Resposta:</strong> Responderemos em até 15 dias úteis (podendo ser prorrogado por mais 15 dias
          em casos complexos).
        </li>
      </ol>
    </section>

    <section id="transferencia" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">8. Transferência Internacional de Dados</h2>
      <p class="mb-4">
        Conforme Art. 33 da LGPD, a transferência de dados pessoais para países fora do Brasil é apenas permitida em
        determinadas circunstâncias:
      </p>
      <ul class="list-disc pl-6 mb-4 space-y-2">
        <li>Para países reconhecidos como adequados pela ANPD</li>
        <li>Quando há salvaguardas apropriadas (cláusulas contratuais padrão, BCRs, etc.)</li>
        <li>Com consentimento explícito do titular</li>
      </ul>
      <p class="mb-4">
        Caso transferências internacionais sejam necessárias, implementamos medidas appropriadas para garantir
        proteção equivalente.
      </p>
    </section>

    <section id="sensiveis" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">9. Proteção de Dados Sensíveis</h2>
      <p class="mb-4">
        Conforme Art. 11 da LGPD, o processamento de dados pessoais sensíveis é proibido, com exceções específicas.
        Se coletamos dados sensíveis, isso ocorre apenas quando:
      </p>
      <ul class="list-disc pl-6 mb-4 space-y-2">
        <li>Você forneceu consentimento específico</li>
        <li>Necessário para cumprimento de obrigação legal</li>
        <li>Para proteção de vida ou integridade física</li>
        <li>Realizado por organizações de direitos humanos</li>
        <li>Dados manifestamente tornados públicos pelo próprio titular</li>
      </ul>
    </section>

    <section id="menores" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">10. Proteção de Dados de Menores</h2>
      <p class="mb-4">
        Conforme Art. 14 da LGPD, o processamento de dados pessoais de menores de 18 (dezoito) anos é permitido
        apenas com consentimento de pelo menos um responsável legal. Nossa Plataforma:
      </p>
      <ul class="list-disc pl-6 mb-4 space-y-2">
        <li>Não coleta intencionalmente dados de menores de 18 anos</li>
        <li>Se descobrir que coletou, exclui os dados imediatamente</li>
        <li>Requer consentimento de responsáveis legais para menores</li>
      </ul>
    </section>

    <section id="anpd" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">11. Autoridade Nacional de Proteção de Dados (ANPD)</h2>
      <p class="mb-4">
        A Autoridade Nacional de Proteção de Dados (ANPD) é o órgão do governo brasileiro responsável por fazer
        cumprir a LGPD. Se você acredita que seus direitos foram violados, pode apresentar uma reclamação à ANPD:
      </p>

      <div class="bg-gray-50 p-6 rounded-lg my-4">
        <p class="mb-2"><strong>Autoridade Nacional de Proteção de Dados (ANPD)</strong></p>
        <p class="mb-2"><strong>Website:</strong> www.gov.br/cidadania/pt-br/acesso-a-informacao/lgpd</p>
        <p class="mb-2"><strong>E-mail:</strong> ouvidoria&#64;anpd.gov.br</p>
        <p><strong>Endereço:</strong> Esplanada dos Ministérios, Bloco C, Edifício Sede, Brasília - DF</p>
      </div>

      <p class="mb-4">
        Você pode entrar em contato com a ANPD para:
      </p>
      <ul class="list-disc pl-6 mb-4 space-y-2">
        <li>Registrar uma reclamação sobre violação da LGPD</li>
        <li>Solicitar orientação sobre seus direitos</li>
        <li>Informar violações de segurança de dados</li>
      </ul>
    </section>

    <section id="sancoes" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">12. Sanções pela Violação da LGPD</h2>
      <p class="mb-4">
        A LGPD estabelece sanções administrativas para violações, incluindo:
      </p>
      <ul class="list-disc pl-6 mb-4 space-y-2">
        <li>Advertência</li>
        <li>Multas de até R$ 50.000.000 (cinquenta milhões de reais) ou até 2% do faturamento da entidade</li>
        <li>Bloqueio dos dados pessoais</li>
        <li>Exclusão dos dados pessoais</li>
        <li>Publicação da infração</li>
      </ul>
      <p class="mb-4">
        Além disso, podem ser aplicadas sanções civis e penais conforme outras leis brasileiras.
      </p>
    </section>

    <section id="faq" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">13. Perguntas Frequentes sobre LGPD</h2>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">Q: Como meus dados são protegidos?</h3>
        <p>
          R: Implementamos medidas técnicas e administrativas de segurança, incluindo criptografia, firewalls,
          controle de acesso e monitoramento contínuo.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">Q: Posso deletar meus dados?</h3>
        <p>
          R: Sim, você tem o direito à exclusão em determinadas circunstâncias. Entre em contato com nosso DPO
          para solicitar a exclusão de seus dados.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">Q: Como obtenho cópia de meus dados?</h3>
        <p>
          R: Você tem direito à portabilidade. Solicite ao DPO seus dados em formato estruturado e legível
          por máquina.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">Q: Por quanto tempo meus dados são retidos?</h3>
        <p>
          R: Retemos seus dados pelo tempo necessário para cumprir com o propósito de coleta e obrigações legais.
          Geralmente, dados de inscrição são mantidos por 5 anos para auditoria.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">Q: Meus dados são compartilhados com terceiros?</h3>
        <p>
          R: Seus dados podem ser compartilhados com órgãos públicos, organizadoras de concursos e operadores
          de dados conforme necessário. Nunca vendemos seus dados.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">Q: E se houver um vazamento de dados?</h3>
        <p>
          R: Seremos notificados imediatamente e você será informado dentro de 2 dias úteis. Investigaremos
          e tomaremos medidas para mitigar danos.
        </p>
      </div>

      <div class="mb-4">
        <h3 class="text-xl font-semibold mb-2">Q: Como faço uma reclamação sobre privacidade?</h3>
        <p>
          R: Entre em contato com nosso DPO (dpo&#64;exemplo.com) ou com a ANPD
          (ouvidoria&#64;anpd.gov.br).
        </p>
      </div>
    </section>

    <section id="conclusao" class="mb-8">
      <h2 class="text-2xl font-bold mt-8 mb-4">14. Conclusão</h2>
      <p class="mb-4">
        A LGPD representa um marco importante na proteção de dados pessoais no Brasil. Nossa Plataforma está
        totalmente comprometida em respeitar e implementar todos os requisitos da LGPD, garantindo que seus
        dados pessoais sejam tratados com transparência, segurança e respeito aos seus direitos fundamentais.
      </p>
      <p class="mb-4">
        Se você tiver dúvidas ou preocupações sobre como seus dados são tratados, entre em contato com nosso
        Encarregado de Proteção de Dados (DPO) nos canais fornecidos. Estamos aqui para ajudar.
      </p>
    </section>

    <div class="bg-gray-100 p-6 rounded-lg mt-12">
      <p class="text-sm text-gray-700">
        <strong>Informação Legal:</strong> Este documento foi preparado em conformidade com a Lei Geral de Proteção de Dados
        Pessoais (LGPD - Lei nº 13.709/2018). Para questões legais específicas, recomenda-se consultar um advogado especializado
        em proteção de dados.
      </p>
    </div>
  </div>
</div>
